import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { buildPath } from "../../utils/menuRouting";
import { resolveMenuComponent } from "../../routes/componentRegistry";
import { decrypt } from "../../utils/encryption";

/**
 * Checks if a path matches a pattern (handling :id segments)
 */
function isPathMatch(pattern, path) {
  if (!pattern || !path) return false;
  const patternSegments = pattern.split("/").filter(Boolean);
  const pathSegments = path.split("/").filter(Boolean);

  if (patternSegments.length !== pathSegments.length) return false;

  return patternSegments.every((seg, i) => {
    if (seg.startsWith(":")) return true;
    return seg.toLowerCase() === pathSegments[i].toLowerCase();
  });
}

/**
 * Recursively searches for the path of menu items leading to the target path
 */
function findMenuPathInTree(menuItems, targetPath) {
  for (const menu of menuItems) {
    const menuPath = buildPath(menu?.controllerName, menu?.actionName);
    
    // Check if this menu matches the target path (handling :id)
    if (isPathMatch(menuPath, targetPath)) {
      return [menu];
    }
    
    // Check children
    if (Array.isArray(menu?.children) && menu.children.length > 0) {
      const childPath = findMenuPathInTree(menu.children, targetPath);
      if (childPath) {
        return [menu, ...childPath];
      }
    }
  }
  return null;
}

const Breadcrumbs = () => {
  const { pathname } = useLocation();
  const { myMenus } = useAuth();

  const breadcrumbs = useMemo(() => {
    if (pathname === "/dashboard" || pathname === "/") {
      return [];
    }

    const crumbs = [
      {
        label: "Dashboard",
        path: "/dashboard",
        isLast: false,
        clickable: true,
      },
    ];

    const menuPath = findMenuPathInTree(myMenus, pathname);

    if (menuPath) {
      menuPath.forEach((menu, index) => {
        const isLast = index === menuPath.length - 1;
        let path = buildPath(menu?.controllerName, menu?.actionName);
        const hasComponent = !!resolveMenuComponent(menu);
        
        if (path && path.includes(":id")) {
           const pathSegments = path.split("/");
           const currentSegments = pathname.split("/");
           path = pathSegments.map((seg, i) => seg.startsWith(":") ? currentSegments[i] : seg).join("/");
        }

        crumbs.push({
          label: menu.menuName,
          path: path || pathname,
          isLast,
          clickable: hasComponent && !!path,
        });
      });
    } else {
      const segments = pathname.split("/").filter(Boolean);
      let currentPath = "";
      segments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        const isLast = index === segments.length - 1;
        
        // Skip encrypted ID segments or numeric IDs from being displayed in breadcrumbs
        // as they are not human-readable labels.
        if (!isNaN(segment) || (segment.length > 20 && decrypt(segment))) {
          return;
        }
        
        const partialMenuPath = findMenuPathInTree(myMenus, currentPath);
        const menu = partialMenuPath ? partialMenuPath[partialMenuPath.length - 1] : null;
        
        // A segment is clickable if it has an associated menu component 
        // OR if it matches a known component in the registry (handling intermediate segments not in menu)
        const hasComponent = menu ? !!resolveMenuComponent(menu) : (
          !!resolveMenuComponent({ controllerName: segment, actionName: "index" }) ||
          (index > 0 ? !!resolveMenuComponent({ controllerName: segments[index - 1], actionName: segment }) : false)
        );

        let label = menu?.menuName || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
        
        if (!menu && !isNaN(segment)) {
          label = "Details";
        }

        if (crumbs.length > 0 && crumbs[crumbs.length - 1].label === label) {
          crumbs[crumbs.length - 1].path = currentPath;
          crumbs[crumbs.length - 1].isLast = isLast;
          crumbs[crumbs.length - 1].clickable = hasComponent || isLast;
        } else {
          crumbs.push({
            label,
            path: currentPath,
            isLast,
            clickable: hasComponent || isLast,
          });
        }
      });
    }

    if (crumbs.length > 0) {
      crumbs.forEach((c, i) => {
        c.isLast = i === crumbs.length - 1;
      });
    }

    return crumbs;
  }, [pathname, myMenus]);

  if (breadcrumbs.length <= 1 && (pathname === "/dashboard" || pathname === "/")) return null;

  return (
    <nav className="flex flex-wrap items-center text-[9px] font-bold uppercase tracking-widest sm:text-[10px]" aria-label="Breadcrumb">
      <ol className="flex items-center">
        {breadcrumbs.map((crumb, index) => (
          <li key={crumb.path + index} className="flex items-center">
            {index > 0 && (
              <svg
                className="mx-2 h-3 w-3 text-slate-300 dark:text-slate-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            )}
            {crumb.isLast || !crumb.clickable ? (
              <span className={`cursor-default ${crumb.isLast ? "text-primary-600 dark:text-primary-400" : "text-slate-400 dark:text-slate-500"}`}>
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.path}
                className="text-slate-400 transition-colors hover:text-primary-600 dark:text-slate-500 dark:hover:text-primary-400"
              >
                {crumb.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
